/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {mat4} from "gl-matrix";
import Messenger from "App/messenger";
// @ts-ignore
//import ChunkedLODWorker from "App/workers/globe.worker";
import GlobeWorker from "App/workers/globe";
import IScene from "js_interface/src/scene_interface";
import IGlobe from "js_interface/src/globe/globe_interface";

export default
class GlobeWorkerManager {
    private sceneAPI: IScene;
    private worker: any;
    private delayedPost: { viewpointChangedMessage: any; messages: any[]; changed: boolean };
    private delayedPostCB: () => void;
    private delayedPostUpdateInterval: number = 0;
    private globe: IGlobe;

    constructor(aSceneAPI: IScene, aGlobe: IGlobe) {
        this.sceneAPI = aSceneAPI;
        this.globe = aGlobe;
        this.worker = null;

        this.initWorker();

        // asynchronous actions in order to handle large amount of changes in batches
        this.delayedPost = {
            viewpointChangedMessage: null,
            messages: [],
            changed: false
        };

        this.delayedPostCB = () => {
            // the following order matters!
            if (this.delayedPost.viewpointChangedMessage) {
                this.delayedPost.messages.unshift(this.delayedPost.viewpointChangedMessage);
                this.delayedPost.viewpointChangedMessage = null;
            }

            if (this.delayedPost.messages.length > 0) {
                this.worker.postMessage({messages: this.delayedPost.messages});
                this.delayedPost.messages = [];
            }

            if (!this.delayedPost.changed) {
                // stop the interval in case we have no request to resolutions recompute
                window.clearInterval(this.delayedPostUpdateInterval);
                this.delayedPostUpdateInterval = 0;
            } else {
                this.delayedPost.changed = false;
            }
        };

        this.delayedPostUpdateInterval = 0;
    }

    public initWorker() {
        //@ts-ignore
        this.worker = new GlobeWorker();
        this.createThreadListener();
    }

    public enableDebug(aValue: boolean) {
        this.worker.postMessage({
            messages: [
                {
                    type: 'enableDebug',
                    enableDebug: aValue
                }
            ]
        });
    }

    public startGlobeRendering(aBase: string, aPatches: any, aModels: any, aZeropointTranslationMatrix: mat4) {
        this.worker.postMessage({
            messages: [
                {
                    type: 'startRendering',
                    origin: window.location.origin,
                    base: aBase,
                    patches: aPatches,
                    models: aModels,
                    zeropointTranslationMatrix: aZeropointTranslationMatrix,
                    isMobile: this.sceneAPI.isMobile()
                }
            ]
        });
    }

    public setUpdatesLimited(aValue: boolean) {
        this.worker.postMessage({
            messages: [
                {
                    type: 'updatesLimited',
                    limited: aValue
                }
            ]
        });
    }

    public schedulePost() {
        if (this.delayedPost.changed) {
            return;
        }

        this.delayedPost.changed = true;
        if (!this.delayedPostUpdateInterval) {
            this.delayedPostUpdateInterval = window.setInterval(this.delayedPostCB, 30);
        }
    }

    public settledOnGPU(aData: any) {
        let query = {
            type: 'settledOnGPU',
            patchInfo: aData.patchInfo,
            uid: aData.uid
        };

        this.delayedPost.messages.push(query);

        this.schedulePost();
    }

    public viewpointChanged() {
        let query = {
            type: 'viewpointChanged',
            views: []
        };

        //collect all eyes, mvp and size of all views and send it to the resolution compute worker
        let viewMode = this.sceneAPI.viewModeModule;
        if (!viewMode) {
            return;
        }

        let views = viewMode.getViews();
        for (let i = 0; i < views.length; i++) {
            let camera = views[i].getCamera();
            query.views.push({
                eye: camera.getCameraPosition(),
                target: camera.getCameraTarget(),
                fov: camera.getFieldOfView(),
                aspect: camera.getAspectRatio(),
                far: camera.getFarValue(),
                vpsize: views[i].size,
                vpm: camera.getViewProjectionMatrix()
            });
        }

        this.delayedPost.viewpointChangedMessage = query;

        this.schedulePost();
    }

    public createThreadListener() {
        this.worker.addEventListener('message',
            function foo(aSelf) {
                // add to the message listener the closure with this instance
                let self = aSelf;

                function computationDone(e) {

                    let messages;
                    if (e.data.type === "messagesBuffer") {
                        messages = e.data.messages;
                    } else {
                        messages = [e.data];
                    }

                    for (let i = 0; i < messages.length; i++) {
                        switch (messages[i].type) {
                            case "visibilityUpdate":
                                self.globe.updateVisibility(messages[i]);
                                break;
                            case "debugDataUpdate":
                                self.globe.updateDebugData(messages[i]);
                                break;
                            case "commit2gpu":
                                self.globe.scheduleCommit2gpu(messages[i]);
                                break;
                            case "removeFromGpu":
                                self.globe.removeFromGpu(messages[i]);
                                break;
                            case "sceneLoaded":
                                Messenger.fireCDWKEvent(self.sceneAPI.getTargetTag(), "cwx3d_sceneLoaded", {});
                                self.sceneAPI.setSceneLoaded(true);
                                break;
                        }
                    }

                }

                return computationDone;
            }(this), false);
    }
}
